import { Injectable, inject, signal } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ClockView, businessPeriod, parseServerTime, serverTime } from './business-time';

@Injectable({ providedIn: 'root' })
export class BusinessClock {
  private readonly backend = inject(HttpBackend, { optional: true });
  readonly message = signal('');
  readonly loading = signal(false);
  retryPath = '';
  private pending?: Promise<boolean>;
  private installed = false;
  private suspended = false;
  async synchronize(force = false): Promise<boolean> {
    if (!force && !this.suspended && serverTime.fresh) return true;
    if (this.pending) return this.pending;
    this.loading.set(true);
    this.pending = this.fetch().finally(() => { this.pending = undefined; this.loading.set(false); });
    return this.pending;
  }
  private async fetch(): Promise<boolean> {
    try {
      if (!this.backend) throw new Error('Clock transport unavailable');
      const began = performance.now();
      const response = await firstValueFrom(new HttpClient(this.backend).get<unknown>(`${environment.apiBaseUrl}/nowjst`, {
        params: { clockRequest: crypto.randomUUID() }, observe: 'response',
        headers: { 'Cache-Control': 'no-store, no-cache', Pragma: 'no-cache' },
      }).pipe(timeout(5000)));
      const epoch = parseServerTime(response.body);
      const age = response.headers.get('Age');
      if (age && (!Number.isFinite(Number(age)) || Number(age) !== 0)) throw new Error('Cached server time');
      const date = response.headers.get('Date');
      if (date && (!Number.isFinite(Date.parse(date)) || Math.abs(Date.parse(date) - epoch) > 5000)) throw new Error('Inconsistent server time header');
      serverTime.accept(epoch, began);
      this.suspended = false;
      this.message.set('');
      return true;
    } catch {
      this.message.set('業務日時を確認できません。入力内容は保持されています。日時を再取得してから操作してください。');
      return false;
    }
  }
  async confirm(view: ClockView, refresh?: () => void | Promise<void>): Promise<boolean> {
    if (view.checking) return false;
    view.checking = true;
    try {
    if (!await this.synchronize(true)) return false;
    const period = businessPeriod();
    if (period !== businessPeriod(serverTime.now() + serverTime.uncertainty)) {
      this.message.set('業務日時の切替直前です。数秒待ってからもう一度操作してください。');
      return false;
    }
    if (view.period && (view.period !== period || view.changed)) {
      if (view.period !== period) await refresh?.();
      view.period = period;
      view.changed = !view.allowReconfirm;
      this.message.set('業務日時の境界を越えました。適用日・予約・金額を確認してから、もう一度操作してください。確認画面では入力画面へ戻って再確認してください。');
      return false;
    }
    view.period = period;
    return true;
    } finally { view.checking = false; }
  }
  install(): void {
    if (this.installed) return;
    this.installed = true;
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.suspended = true;
      else void this.synchronize(true);
    });
    window.addEventListener('pageshow', event => { if (event.persisted) void this.synchronize(true); });
  }
}
